import { useEffect, useState } from "react";
import { AlertTriangle, Bell, Cloud, Loader2, MapPin, Radio, User as UserIcon, Wind } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import RadarSweep from "@/components/RadarSweep";
import HUDFrame from "@/components/HUDFrame";
import CalendarConnect from "@/components/CalendarConnect";

const verdictStyles = {
  green: { border: "rgba(78,139,124,0.5)", color: "#4E8B7C", label: "cleared" },
  amber: { border: "rgba(217,138,68,0.5)", color: "#D98A44", label: "watch" },
  red:   { border: "rgba(183,85,85,0.5)",  color: "#B75555", label: "red landing" },
};

const fmtWhen = (start, allDay) => {
  if (!start) return "—";
  const d = new Date(start);
  const now = Date.now();
  const diffH = (d.getTime() - now) / 3600000;
  const rel =
    diffH < 24 ? `T-${Math.max(0, Math.round(diffH))}h`
    : diffH < 24 * 14 ? `T-${Math.round(diffH / 24)}d`
    : d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
  const time = allDay
    ? "all day"
    : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${rel} · ${time}`;
};

export default function Radar() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);
  const [assessBusy, setAssessBusy] = useState(false);

  const load = () => {
    api.get("/calendar/status").then((r) => setStatus(r.data)).catch(() => {});
    api
      .get("/calendar/events?days=30&household=true")
      .then((r) => setEvents(r.data.events || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Radar unavailable."));
  };
  useEffect(load, []);

  const runAssess = async () => {
    setAssessBusy(true);
    try {
      const { data } = await api.post("/household/radar/assess");
      toast.success(`Radar re-assessed ${data.assessed} events. ${data.new_reds} new red landing${data.new_reds === 1 ? "" : "s"}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Radar assessment failed.");
    } finally {
      setAssessBusy(false);
    }
  };

  const [rescheduleBusy, setRescheduleBusy] = useState({});
  const rescheduleToGreen = async (evId) => {
    if (!window.confirm("Move this event to the greenest available day (next 14 days)? The original duration is preserved.")) return;
    setRescheduleBusy((s) => ({ ...s, [evId]: true }));
    try {
      const { data } = await api.post(`/calendar/events/${evId}/reschedule-green`);
      toast.success(
        `Moved to ${data.new_day.charAt(0).toUpperCase() + data.new_day.slice(1)}. ${data.reason}`,
        data.html_link ? { action: { label: "Open", onClick: () => window.open(data.html_link, "_blank") } } : undefined,
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reschedule.");
    } finally {
      setRescheduleBusy((s) => ({ ...s, [evId]: false }));
    }
  };

  if (err) return <div className="mx-auto max-w-3xl px-6 py-16 text-mist" data-testid="radar-error">{String(err)}</div>;
  if (events === null || status === null)
    return (
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-16 font-mono text-[11px] uppercase tracking-[0.22em] text-mist" data-testid="radar-loading">
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        Scanning the horizon…
      </div>
    );

  const connected = events.length > 0 || status.connected;
  const redCount = events.filter((e) => e.landing_verdict === "red").length;
  const amberCount = events.filter((e) => e.landing_verdict === "amber").length;

  return (
    <div data-testid="radar-page" className="mx-auto max-w-7xl px-6 py-10 md:px-8 md:py-12">
      <div className="grid gap-10 md:grid-cols-12">
        <div className="md:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
            R-02 · Radar
          </p>
          <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
            The horizon,
            <br />
            <span className="italic text-mist">before it arrives.</span>
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-mist">
            {connected
              ? `Live events from every connected calendar in the household — the next 30 days. Each carries a landing verdict from the tower.`
              : "Connect Google Calendar to see your real upcoming events on the tower."}
          </p>

          {connected && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="radar-verdict-summary">
              <span className="border border-signalRed/50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-signalRed">
                {redCount} red
              </span>
              <span className="border border-signalAmber/50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-signalAmber">
                {amberCount} amber
              </span>
              <button
                onClick={runAssess}
                disabled={assessBusy}
                data-testid="radar-assess-btn"
                className="inline-flex items-center gap-1.5 border border-rule px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink disabled:opacity-50"
              >
                <Radio className="h-3 w-3" strokeWidth={1.5} />
                {assessBusy ? "Sweeping…" : "Re-sweep"}
              </button>
            </div>
          )}

          <div className="relative mt-8 max-w-md">
            <span className="absolute -top-2 -left-2 h-4 w-4 border-t border-l border-teal/70" />
            <span className="absolute -bottom-2 -right-2 h-4 w-4 border-b border-r border-teal/70" />
            <div className="border border-rule bg-deep/60 p-8">
              <RadarSweep />
              <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                <span>Range · 30d</span>
                <span className={connected ? "text-signalGreen" : "text-mist"}>
                  {connected ? "Live sweep" : "No feed"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          {!connected ? (
            <CalendarConnect />
          ) : events.length === 0 ? (
            <HUDFrame className="bg-deep/40 p-8" tone="mist">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                <Cloud className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
                Nothing on the horizon
              </div>
              <p className="mt-4 text-sm leading-relaxed text-mist">
                No events found in the next 30 days across your calendars. Enjoy the
                quiet airspace.
              </p>
            </HUDFrame>
          ) : (
            <HUDFrame className="bg-deep/40 p-8" tone="teal">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                <Cloud className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
                Approaching · {events.length} event{events.length === 1 ? "" : "s"}
              </div>
              <ul className="mt-6 divide-y divide-rule" data-testid="radar-events">
                {events.slice(0, 30).map((ev) => {
                  const v = verdictStyles[ev.landing_verdict] || verdictStyles.green;
                  return (
                    <li key={`${ev.event_id}-${ev.user_id || "u"}`} className="flex gap-6 py-5" data-testid={`radar-event-${ev.event_id}`}>
                      <span className="min-w-[90px] font-mono text-[11px] uppercase tracking-[0.22em] text-teal">
                        {fmtWhen(ev.start, ev.all_day)}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-display text-lg text-ink">
                            {ev.summary}
                          </span>
                          <span
                            className="border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em]"
                            style={{ borderColor: v.border, color: v.color }}
                            data-testid={`radar-verdict-${ev.event_id}`}
                          >
                            {v.label}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                          {ev.member_name && (
                            <span className="flex items-center gap-1 text-teal">
                              <UserIcon className="h-3 w-3" strokeWidth={1.5} />
                              {ev.member_name}
                            </span>
                          )}
                          {ev.calendar_name && <span>{ev.calendar_name}</span>}
                          {ev.location && (
                            <>
                              <span>·</span>
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" strokeWidth={1.5} />
                                {ev.location}
                              </span>
                            </>
                          )}
                          {ev.attendee_count > 0 && (
                            <>
                              <span>·</span>
                              <span>{ev.attendee_count} attendees</span>
                            </>
                          )}
                        </div>
                        {ev.landing_mitigation && ev.landing_verdict !== "green" && (
                          <p className="mt-2 text-sm leading-relaxed text-mist">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist/70">Mitigation · </span>
                            {ev.landing_mitigation}
                          </p>
                        )}
                        {ev.landing_verdict === "red" && (
                          <button
                            onClick={() => rescheduleToGreen(ev.event_id)}
                            disabled={!!rescheduleBusy[ev.event_id]}
                            data-testid={`reschedule-green-${ev.event_id}`}
                            title="Move this event to the greenest available day in the next 14 days."
                            className="mt-3 inline-flex items-center gap-1.5 border border-signalGreen/60 bg-signalGreen/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-signalGreen transition-colors duration-300 hover:bg-signalGreen/20 disabled:opacity-50"
                          >
                            <Wind className="h-3 w-3" strokeWidth={1.5} />
                            {rescheduleBusy[ev.event_id] ? "Rescheduling…" : "Reschedule to a green day"}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </HUDFrame>
          )}

          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <HUDFrame className="bg-deep/30 p-6" tone="mist">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-signalAmber">
                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
                Weather ahead
              </div>
              <p className="mt-4 text-sm leading-relaxed text-mist">
                {connected
                  ? "Radar re-runs every 15 minutes. New red landings trigger a gentle nudge email to the calendar owner."
                  : "Radar will fold real events into your signals as soon as you connect a calendar."}
              </p>
            </HUDFrame>

            <HUDFrame className="bg-deep/30 p-6" tone="mist">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
                <Bell className="h-3.5 w-3.5" strokeWidth={1.5} />
                Quiet mode
              </div>
              <p className="mt-4 text-sm leading-relaxed text-mist">
                Radar only interrupts you at threshold crossings — items above
                are visible, none pushed to your phone.
              </p>
            </HUDFrame>
          </div>
        </div>
      </div>

      {connected && (
        <div className="mt-10 border-t border-rule pt-8">
          <CalendarConnect />
        </div>
      )}
    </div>
  );
}
